import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

import { ManualCreateExpenseDialog } from "../add-expense-manual/expense-dialog";
import { useCreateExpense } from "@/hooks/finance/useExpenses";
import { useDepartments } from "@/hooks/finance/useDepartments";
import { BankAccount } from "@/hooks/use-bank-accounts";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import {
  PAGE_SIZE,
  fmtMoney,
  fmtDate,
  dateMatchLabel,
  rangeLabel,
  ScoreBadge,
  LoadingBlock,
  EmptyBlock,
  ErrorBlock,
  TableShell,
} from "./matching-ui";

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-black tracking-wide text-slate-500">
      {children}
    </div>
  );
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  const up = String(s).toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

const getRefOverlapTokens = (a?: string | null, b?: string | null): string[] => {
  const A = extractRefTokens(a);
  const B = extractRefTokens(b);
  if (!A.length || !B.length) return [];

  const setB = new Set(B);
  return A.filter((t) => setB.has(t));
};

function HighlightedReference({
  value,
  matchedTokens,
}: {
  value?: string | null;
  matchedTokens: string[];
}) {
  if (!value) return <span className="text-slate-400">—</span>;

  if (!matchedTokens.length) {
    return <span className="break-all">{value}</span>;
  }

  const uniqueTokens = Array.from(new Set(matchedTokens)).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${uniqueTokens.map(escapeRegExp).join("|")})`, "gi");
  const parts = value.split(pattern);

  return (
    <span className="break-all leading-6">
      {parts.map((part, idx) => {
        const isMatch = uniqueTokens.some((t) => t.toUpperCase() === part.toUpperCase());

        if (!isMatch) {
          return <React.Fragment key={idx}>{part}</React.Fragment>;
        }

        return (
          <span
            key={idx}
            className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.5 mx-[1px]",
              "bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200",
              "font-bold"
            )}
          >
            {part}
          </span>
        );
      })}
    </span>
  );
}

function PairHeader({
  leftTitle,
  rightTitle,
  leftLabel = "Bank transaction",
  rightLabel = "Suggested expense matches",
  leftBadge,
  rightBadge,
}: {
  leftTitle: React.ReactNode;
  rightTitle: React.ReactNode;
  leftLabel?: string;
  rightLabel?: string;
  leftBadge?: React.ReactNode;
  rightBadge?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="px-3 py-2 bg-slate-50/70">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-wide text-slate-500">
                {leftLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {leftTitle}
              </div>
            </div>
            {leftBadge ? <div className="shrink-0">{leftBadge}</div> : null}
          </div>
        </div>

        <div className="px-3 py-2 border-t sm:border-t-0 sm:border-l border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-wide text-slate-500">
                {rightLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {rightTitle}
              </div>
            </div>
            {rightBadge ? <div className="shrink-0">{rightBadge}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRow({
  label,
  left,
  right,
  match,
}: {
  label: string;
  left: React.ReactNode;
  right: React.ReactNode;
  match?: React.ReactNode;
}) {
  return (
    <tr className="align-top">
      <td className="px-3 py-2 text-[10px] font-black tracking-wide text-slate-500 w-[120px]">
        {label}
      </td>
      <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
        {left}
      </td>
      <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
        {right}
      </td>
      <td className="px-3 py-2 text-center w-[110px]">
        {match ?? <span className="text-slate-300 text-[12px] font-semibold">—</span>}
      </td>
    </tr>
  );
}

function BankDetailsTable({
  tx,
  candidate,
}: {
  tx: any;
  candidate?: any | null;
}) {
  const txRef = tx.transaction_id_reference ?? "";
  const expRef = candidate?.payment_reference ?? "";
  const overlapTokens = candidate ? getRefOverlapTokens(expRef, txRef) : [];

  const paidDate = candidate ? candidate.payment_date ?? candidate.expense_date : null;
  const expAmount = candidate ? candidate.net_payment ?? candidate.total_amount : null;
  const expCurrency = candidate?.currency ?? "";

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full table-fixed">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide w-[120px]">
              Field
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide">
              Bank
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide">
              Expense
            </th>
            <th className="px-3 py-2 text-center text-[10px] font-black text-slate-500 tracking-wide w-[110px]">
              Match
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          <FieldRow
            label="Amount"
            left={
              <span className="tabular-nums">
                {tx.transaction_amount_currency
                  ? `${tx.transaction_amount_currency} ${fmtMoney(tx.transaction_amount)}`
                  : fmtMoney(tx.transaction_amount)}
              </span>
            }
            right={
              candidate ? (
                <span className="tabular-nums">
                  {expCurrency ? `${expCurrency} ${fmtMoney(expAmount)}` : fmtMoney(expAmount)}
                </span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            match={candidate ? <ScoreBadge score={candidate.score} /> : undefined}
          />

          <FieldRow
            label="Fee"
            left={
              tx.transaction_fee != null ? (
                <span className="tabular-nums">{fmtMoney(tx.transaction_fee)}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            right={<span className="text-slate-400">—</span>}
          />

          <FieldRow
            label="Date"
            left={fmtDate(tx.transaction_date)}
            right={
              candidate && paidDate ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span>{fmtDate(paidDate)}</span>
                  <Badge variant="outline" className="text-[11px] font-medium">
                    {dateMatchLabel(tx.transaction_date, paidDate)}
                  </Badge>
                </div>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />

          <FieldRow
            label="Reference ID"
            left={<HighlightedReference value={txRef} matchedTokens={overlapTokens} />}
            right={
              candidate ? (
                <HighlightedReference value={expRef} matchedTokens={overlapTokens} />
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />

          <FieldRow
            label="Expense #"
            left={<span className="text-slate-400">—</span>}
            right={
              candidate?.expense_number ? (
                <span className="font-mono text-[11px]">{candidate.expense_number}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />

          <FieldRow
            label="Category"
            left={<span className="text-slate-400">—</span>}
            right={
              candidate?.category_name ? (
                <span>{candidate.category_name}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />

          <FieldRow
            label="Description"
            left={
              tx.description_details ? (
                <span className="truncate block max-w-[200px]" title={tx.description_details}>{tx.description_details}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            right={
              candidate?.description ? (
                <span className="truncate block max-w-[200px]" title={candidate.description}>{candidate.description}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />
        </tbody>
      </table>
    </div>
  );
}

function CandidateCard({
  tx,
  candidate,
  idx,
}: {
  tx: any;
  candidate: any;
  idx: number;
}) {
  const whyText =
    Array.isArray(candidate.reasons) && candidate.reasons.length
      ? candidate.reasons.slice(0, 3).join(" · ")
      : "";

  return (
    <div
      className={cn(
        "rounded-lg border p-2.5 sm:p-3 shadow-sm",
        idx === 0 || candidate.score >= 95
          ? "border-emerald-200 bg-emerald-50/30"
          : "border-slate-200 bg-white"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-baseline gap-3">
          <div className="text-[11px] font-semibold text-slate-500 tabular-nums shrink-0">
            #{idx + 1}
          </div>

          {whyText ? (
            <div className="min-w-0 text-[11px] text-slate-700 truncate">
              <span className="font-black text-slate-900">Why:</span>{" "}
              <span className="font-semibold">{whyText}</span>
            </div>
          ) : null}
        </div>

        <div className="shrink-0">
          <ScoreBadge score={candidate.score} />
        </div>
      </div>

      <div className="mt-2">
        <PairHeader
          leftTitle={tx.description_details ?? "Bank transaction"}
          rightTitle={candidate.vendor_name ?? "Vendor"}
          leftLabel="Bank transaction"
          rightLabel="Suggested expense"
        />
      </div>

      <div className="mt-2">
        <BankDetailsTable tx={tx} candidate={candidate} />
      </div>
    </div>
  );
}

function EmptyCandidateCard({ tx }: { tx: any }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3 shadow-sm">
      <div className="mt-0">
        <PairHeader
          leftTitle={tx.description_details ?? "Bank transaction"}
          rightTitle="No matching expense found"
          leftLabel="Bank transaction"
          rightLabel="Suggested expense"
        />
      </div>

      <div className="mt-2">
        <BankDetailsTable tx={tx} candidate={null} />
      </div>

      <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
        No matching expense found in this date window.
      </div>
    </div>
  );
}

export function TabUnmatchedBank({
  isLoading,
  error,
  unmatchedBank,
  pageUnmatchedBank,
  setPageUnmatchedBank,
  totalUB,
  categories,
  refetchUnmatchedBank,
  accounts,
}: {
  isLoading: boolean;
  error: unknown;
  unmatchedBank: any[];
  pageUnmatchedBank: number;
  setPageUnmatchedBank: (p: number) => void;
  totalUB: number;
  categories: {
    id: string;
    name: string;
    subcategories?: { id: string; name: string }[];
  }[];
  refetchUnmatchedBank?: (() => Promise<void> | void) | ((...args: any[]) => any);
  accounts: BankAccount[];
}) {
  const ubItems = React.useMemo(
    () =>
      unmatchedBank.slice(
        (pageUnmatchedBank - 1) * PAGE_SIZE,
        pageUnmatchedBank * PAGE_SIZE
      ),
    [unmatchedBank, pageUnmatchedBank]
  );

  const ubRange = rangeLabel(pageUnmatchedBank, unmatchedBank.length);

  const [selectedTx, setSelectedTx] = React.useState<any | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [isCreatingExpense, setIsCreatingExpense] = React.useState(false);
  const createExpense = useCreateExpense();
  const { data: departments = [] } = useDepartments();

  const openCreateExpenseDialog = (tx: any) => {
    setSelectedTx(tx);
    setIsCreateDialogOpen(true);
  };

  const handleCreateExpense = async (payload: any) => {
    try {
      setIsCreatingExpense(true);

      const created = await createExpense.mutateAsync({
        category_id: payload.category_id,
        department_id: payload.department_id,
        vendor_name: payload.vendor_name,
        expense_date: payload.expense_date,
        payment_date: payload.payment_date,
        description:
          payload.notes?.trim() ||
          selectedTx?.description_details?.trim() ||
          "Manual expense",
        amount: payload.base_amount ?? payload.amount,
        currency: payload.currency || "ETB",
        total_amount: payload.total_amount ?? payload.amount,
        net_payment: payload.net_payment ?? payload.amount,
        vat_rate: payload.vat_rate ?? 0,
        vat_amount: payload.vat_amount ?? 0,
        withholding_rate: payload.withholding_rate ?? 0,
        withholding_amount: payload.withholding_amount ?? 0,
        payment_method: payload.payment_method || undefined,
        reference_number: payload.reference_id || undefined,
        payment_reference: payload.payment_reference,
        bank_account_id: payload.bank_account_id || undefined,
        business_unit: payload.business_unit || undefined,
      });

      if (payload.bu_allocations && payload.bu_allocations.length > 0 && (created as any)?.id) {
        const { error: allocErr } = await (supabase as any).rpc('set_expense_bu_allocations', {
          p_expense_id: (created as any).id,
          p_allocations: payload.bu_allocations,
        });
        if (allocErr) toast.error(`Saved expense, but allocations failed: ${allocErr.message}`);
      }

      setIsCreateDialogOpen(false);
      setSelectedTx(null);
      await refetchUnmatchedBank?.();
    } catch (e) {
      console.error("Failed to create manual expense", e);
    } finally {
      setIsCreatingExpense(false);
    }
  };

  const selectedBankAccountId =
    selectedTx?.bank_account_id != null ? String(selectedTx.bank_account_id) : null;

  const selectedBankName =
    accounts.find((a) => String(a.id) === selectedBankAccountId)?.name ?? null;

  return (
    <TabsContent value="unmatched-bank" className="p-4 pt-4 space-y-2">
      <div className="text-[11px] text-slate-600">
        Bank transactions that are not linked to an expense yet. We show the most likely expense matches.
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <LoadingBlock />
        </div>
      ) : error ? (
        <ErrorBlock text="Failed to load matching suggestions." />
      ) : unmatchedBank.length === 0 ? (
        <EmptyBlock text="No unmatched bank transactions in this range." />
      ) : (
        <TableShell
          page={pageUnmatchedBank}
          setPage={setPageUnmatchedBank}
          totalPages={totalUB}
          showing={ubRange}
          total={unmatchedBank.length}
          keyPrefix="ub"
        >
          <div className="space-y-2">
            {ubItems.map((tx: any, rowIdx: number) => {
              const globalRowNo = (pageUnmatchedBank - 1) * PAGE_SIZE + rowIdx + 1;
              const hasCandidates = Array.isArray(tx.candidates) && tx.candidates.length > 0;

              return (
                <div
                  key={tx.id ?? tx.bank_transaction_id ?? rowIdx}
                  className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-baseline gap-3">
                      <div className="text-[11px] font-semibold text-slate-500 tabular-nums shrink-0">
                        #{globalRowNo}
                      </div>

                      <div className="min-w-0 text-[11px] text-slate-700 truncate">
                        <span className="font-black text-slate-900">Status:</span>{" "}
                        <span className="font-semibold">Unmatched bank transaction</span>
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-2">
                      {hasCandidates ? <ScoreBadge score={tx.best_score ?? 0} /> : null}

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openCreateExpenseDialog(tx)}
                        className="gap-1.5"
                      >
                        <Plus className="h-4 w-4" />
                        Create expense
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2">
                    {hasCandidates ? (
                      <div className="space-y-2">
                        {tx.candidates.map((candidate: any, idx: number) => (
                          <CandidateCard
                            key={candidate.expense_id ?? idx}
                            tx={tx}
                            candidate={candidate}
                            idx={idx}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyCandidateCard tx={tx} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TableShell>
      )}
      <ManualCreateExpenseDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        transaction={selectedTx}
        categories={categories}
        departments={departments}
        isSubmitting={isCreatingExpense}
        bankAccountId={selectedBankAccountId}
        bankName={selectedBankName}
        onSubmit={handleCreateExpense}
      />
    </TabsContent>
  );
}